<template>
  <transition name="fade">
    <!-- Backdrop click (click.self, blocking mode only — the floating wrapper
         is pointer-events: none) only closes for a visitor who has already
         decided and reopened from the footer — a first-time dismissal must
         never pass as an answer. -->
    <div
      v-if="visible"
      ref="dialog"
      class="CookieConsent focus:outline-none"
      :class="blocking ? 'CookieConsent--blocking' : 'CookieConsent--floating'"
      role="dialog"
      :aria-modal="blocking ? 'true' : 'false'"
      :aria-labelledby="showPreferences ? 'cookieConsentTitle' : null"
      :aria-label="showPreferences ? null : 'We use cookies'"
      tabindex="-1"
      @keydown.esc="closeIfDecided"
      @keydown.tab="trapFocus"
      @click.self="closeIfDecided"
    >
      <div class="CookieConsent__panel">
        <!-- No title on the first layer — the whole card is one short sentence
             (the aria-label above names the dialog for screen readers). The
             manage view keeps its heading. -->
        <div v-if="showPreferences" id="cookieConsentTitle" class="text-xl font-semibold leading-snug">Cookie Settings</div>

        <!-- Essential cookies aren't mentioned here: they need no consent, and
             the manage view discloses them. The two optional purposes must
             stay named for the consent to be informed. -->
        <div v-if="!showPreferences" class="text-muted text-base">
          We use cookies to understand which features help riders and to measure our ads.
          <button class="underline" @click="showPreferences = true">Learn more and manage</button>
        </div>

        <div v-if="gpc" class="sm:text-base text-muted mt-2 text-sm">
          Your browser sends a Do Not Sell or Share signal, so analytics and advertising are already switched off. You can still turn them on here.
        </div>

        <!-- The label text lives inside each Checkbox, so the whole row is one
             click target. Inline align-items beats the component's own
             items-center, which would centre the box against three lines. -->
        <div v-if="showPreferences" class="mt-4 space-y-1 text-sm">
          <Checkbox :value="true" disabled style="display: flex; align-items: flex-start">
            <div class="font-medium">Essential</div>
            <div class="text-muted">Sign-in, security and your preferences. Always active.</div>
          </Checkbox>

          <Checkbox v-model="analytics" style="display: flex; align-items: flex-start">
            <div class="font-medium">Analytics</div>
            <div class="text-muted">Feature usage and errors. PostHog, Google Analytics.</div>
          </Checkbox>

          <Checkbox v-model="marketing" style="display: flex; align-items: flex-start">
            <div class="font-medium">Advertising</div>
            <div class="text-muted">Ad conversion measurement. Google Ads.</div>
          </Checkbox>

          <!-- The single first-layer link keeps the card clean, so the full
               detail and the withdrawal notice live here in the manage layer.
               Kept out of Prettier's hands so the closing tag hugs the full
               stop — a line break there renders as "Privacy Policy ." -->
          <!-- prettier-ignore -->
          <div class="text-muted text-xs">
            Change your mind any time from the footer. Full details in our
            <router-link :to="{ name: 'privacy' }" class="underline">Privacy Policy</router-link>.
          </div>
        </div>

        <!-- Two stacked buttons; granular choice lives one tap away behind
             "Learn more and manage". Keeping the categories reachable from the
             first layer is what keeps a bundled Accept specific enough to stand
             as consent. When the manage view is open, saving the toggles is the
             primary action, so Accept all steps back to secondary. -->
        <!-- First layer: two equal buttons side by side. Manage view: three
             actions, stacked. w-full on flex-row children shrinks to an even
             split, so the same classes serve both layouts. -->
        <div class="sm:mt-6 flex gap-3 mt-4" :class="showPreferences ? 'flex-col' : 'flex-row'">
          <AppButton v-if="showPreferences" small primary class="w-full" :disabled="saving" @click="save('save_preferences', analytics, marketing)">
            Save choices
          </AppButton>

          <AppButton small :primary="!showPreferences" :secondary="showPreferences" class="w-full" :disabled="saving" @click="save('accept_all', true, true)">
            Accept all
          </AppButton>
          <AppButton small secondary class="w-full" :disabled="saving" @click="save('reject_all', false, false)">Reject</AppButton>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import bus from 'bus'
import sentry from 'sentry'

import { consentState, needsConsentBanner, saveConsent } from 'utils/consent'

export default {
  name: 'CookieConsent',

  data() {
    const state = consentState()

    return {
      visible: needsConsentBanner(),
      showPreferences: false,
      analytics: state.analytics,
      marketing: state.marketing,
      gpc: state.gpc,
      blocking: state.blocking,
      saving: false,
      returnFocusTo: null
    }
  },

  created() {
    bus.$on('consent:open', this.open)
  },

  mounted() {
    // A consent choice is not optional decoration, so move focus to it when it
    // appears — screen reader users would otherwise never meet it, since the
    // banner renders last in the document.
    if (this.visible) {
      this.lockScroll()
      this.focusDialog()
    }
  },

  beforeDestroy() {
    bus.$off('consent:open', this.open)
    this.unlockScroll()
  },

  methods: {
    // Reopened from the footer, so the visitor can change or withdraw a choice.
    open() {
      const state = consentState()

      this.returnFocusTo = document.activeElement
      this.analytics = state.analytics
      this.marketing = state.marketing
      this.gpc = state.gpc
      this.showPreferences = true
      this.visible = true
      this.lockScroll()
      this.focusDialog()
    },

    focusDialog() {
      // preventScroll: the panel is already in view, and scrolling the page out
      // from under someone who has not asked for anything is disorienting.
      this.$nextTick(() => this.$refs.dialog?.focus({ preventScroll: true }))
    },

    // In blocking mode the dialog is modal, so Tab and Shift+Tab wrap within
    // it instead of escaping into the inert page behind the backdrop. In
    // floating mode the page is live, so focus flows naturally.
    trapFocus(e) {
      if (!this.blocking) return

      const focusables = this.$refs.dialog.querySelectorAll('a[href], button:not([disabled]), input:not([disabled])')

      if (!focusables.length) return

      const first = focusables[0]
      const last = focusables[focusables.length - 1]

      if (e.shiftKey && (document.activeElement === first || document.activeElement === this.$refs.dialog)) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    },

    // Blocking mode only. Root and body both, for iOS Safari's sake.
    // unlockScroll stays unconditional — clearing an untouched style is a
    // no-op, and it keeps every close path safe in either mode.
    lockScroll() {
      if (!this.blocking) return

      document.documentElement.style.overflow = 'hidden'
      document.body.style.overflow = 'hidden'
    },

    unlockScroll() {
      document.documentElement.style.overflow = ''
      document.body.style.overflow = ''
    },

    // Escape only exits the reopened panel. Before a first choice it does
    // nothing, so a dismissal can never be mistaken for an answer.
    closeIfDecided() {
      if (consentState().decided) this.close()
    },

    close() {
      this.visible = false
      this.showPreferences = false
      this.unlockScroll()

      const target = this.returnFocusTo
      this.returnFocusTo = null

      this.$nextTick(() => target?.focus?.())
    },

    async save(choice, analytics, marketing) {
      this.saving = true

      try {
        await saveConsent({ analytics, marketing, choice })
      } catch (e) {
        // The choice was not recorded, so no cookie was written and no tag was
        // switched on — the banner simply comes back on the next page load.
        sentry.captureException(e)
      }

      this.saving = false
      this.close()
    }
  }
}
</script>

<style>
/* Two presentation modes, chosen server-side (ENV COOKIE_CONSENT_BLOCKING via
   ConsentTracking#blocking_consent?). Both share the same card. */
.CookieConsent {
  @apply fixed z-50 flex;
}

/* Non-blocking (default): a card floating bottom-right on desktop, bottom
   sheet on mobile. The wrapper is a full-width strip, so clicks must fall
   through its invisible area to the page below — only the card is
   interactive. */
.CookieConsent--floating {
  @apply sm:p-8 bottom-0 left-0 justify-end w-full p-0;
  pointer-events: none;
}

.CookieConsent--floating .CookieConsent__panel {
  pointer-events: auto;
}

/* Extra-large screens keep the roomier corner offset. */
@media (min-width: 1600px) {
  .CookieConsent--floating {
    @apply p-12;
  }
}

/* Blocking: the wrapper is the backdrop, dimming and intercepting the whole
   page until the visitor answers. Card centered on desktop; edge-to-edge
   bottom sheet on mobile. */
.CookieConsent--blocking {
  @apply sm:items-center sm:p-8 inset-0 items-end justify-center p-0;
  background: rgba(15, 23, 42, 0.5);
}

/* Mobile: top corners rounded, top border only. From sm up: a centered card.
   26rem is not a stock Tailwind step, so the cap is plain CSS, not @apply. */
.CookieConsent__panel {
  @apply sm:rounded-lg sm:border sm:p-7 rounded-t-xl w-full p-6 bg-white border-t;
  /* Tailwind's shadows cast downward — useless for a bottom sheet, which needs
     lift *above* white page content. Large, soft ambient shadow, upward bias. */
  box-shadow: 0 -12px 60px rgba(0, 0, 0, 0.1), 0 -2px 16px rgba(0, 0, 0, 0.05);
}

@media (min-width: 640px) {
  .CookieConsent__panel {
    max-width: 26rem;
  }
}

/* Banner-scoped button palette: neutral black/outline instead of the app's
   orange, so the consent card reads as chrome rather than as marketing. */
.CookieConsent .button {
  @apply rounded-full;
}

.CookieConsent .button.isPrimary,
.CookieConsent .button.isPrimary:hover {
  @apply text-white bg-gray-900 border-gray-900;
}

.CookieConsent .button.isPrimary:hover {
  opacity: 0.9;
}

.CookieConsent .button.isSecondary,
.CookieConsent .button.isSecondary:hover {
  @apply text-gray-900 bg-white border-gray-700;
}

.CookieConsent .button.isSecondary:hover {
  @apply bg-gray-100;
}

/* Category rows read as one hoverable unit. The negative horizontal margin
   keeps the text aligned with the copy above while the hover pill bleeds out;
   only margin-left/right, so it cannot fight the container's space-y. */
.CookieConsent .Checkbox {
  @apply px-3 py-2 transition-colors duration-150 rounded-md cursor-pointer;
  margin-left: -0.75rem;
  margin-right: -0.75rem;
}

.CookieConsent .Checkbox:hover {
  @apply bg-gray-200;
}

/* The box is a flex sibling of a multi-line label here, so stop it shrinking.
   top: 1px counters the component's -1px nudge and drops the box to sit
   optically centered on the title line. */
.CookieConsent .Checkbox__virtualInput {
  @apply flex-shrink-0;
  top: 1px;
}

/* Essential cannot be turned off — no pointer, no hover invitation. */
.CookieConsent .Checkbox:has(.Checkbox__input:disabled) {
  @apply cursor-default;
}

.CookieConsent .Checkbox:has(.Checkbox__input:disabled):hover {
  background: transparent;
}

.CookieConsent .Checkbox__input:disabled ~ * {
  @apply cursor-default;
}

/* .button and .Checkbox__input both apply outline-none globally, so a keyboard
   user gets no visible focus at all. Restore it here — a consent choice you
   cannot see yourself landing on is not really a choice. The checkbox input is
   opacity-0, so the ring has to go on the box that is actually drawn. */
.CookieConsent button:focus-visible,
.CookieConsent a:focus-visible,
.CookieConsent .Checkbox__input:focus-visible + .Checkbox__virtualInput {
  outline: 2px solid theme('colors.link');
  outline-offset: 2px;
}

.fade-enter-active,
.fade-leave-active {
  @apply transition-opacity duration-200;
}

.fade-enter,
.fade-leave-to {
  @apply opacity-0;
}
</style>
